import { GeckoEntryBase } from 'types/entries/geckoEntry'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseFiniteNumber, parseString } from 'utils/parsingHelpers'

export const KestrelDefectReviewStatus = {
  NeedsReview: 'needs-review',
  Accepted: 'accepted',
  Rejected: 'rejected',
  Deleted: 'deleted',
} as const

export type KestrelDefectReviewStatus =
  (typeof KestrelDefectReviewStatus)[keyof typeof KestrelDefectReviewStatus]

export const KestrelDefectDetectionRunState = {
  Initializing: 'initializing',
  Queued: 'queued',
  Staging: 'staging',
  BatchSubmitting: 'batch_submitting',
  BatchSubmitted: 'batch_submitted',
  BatchRunning: 'batch_running',
  DeconflictionStarted: 'deconfliction_started',
  DeconflictionComplete: 'deconfliction_complete',
  Failed: 'failed',
} as const

export type KestrelDefectDetectionRunState =
  (typeof KestrelDefectDetectionRunState)[keyof typeof KestrelDefectDetectionRunState]

export interface KestrelDefectReview {
  best_image: string | null
  class_name_override?: string | null
  status: KestrelDefectReviewStatus
}

export type KestrelDefectReviews = Record<string, KestrelDefectReview>

export interface KestrelDefectDetectionRunMetadata {
  state?: KestrelDefectDetectionRunState
  created_at?: string
  completed_at?: string
  deconfliction_completed_at?: string
  model?: string
  model_type?: string
  vertex_model_id?: string
  confidence_threshold?: number
  clustering_distance_m?: number
  error?: string
  failure_stage?: string
  image_count?: number
}

export type KestrelDefectDetectionRunData = {
  run_id: string
  metadata: KestrelDefectDetectionRunMetadata
  result: Record<string, unknown>
  reviews: KestrelDefectReviews
}

const parseRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const parseReviewStatus = (
  value: unknown
): KestrelDefectReviewStatus | undefined => {
  const status = parseString(value)
  return Object.values(KestrelDefectReviewStatus).includes(
    status as KestrelDefectReviewStatus
  )
    ? (status as KestrelDefectReviewStatus)
    : undefined
}

const parseRunState = (
  value: unknown
): KestrelDefectDetectionRunState | undefined => {
  const state = parseString(value)
  return Object.values(KestrelDefectDetectionRunState).includes(
    state as KestrelDefectDetectionRunState
  )
    ? (state as KestrelDefectDetectionRunState)
    : undefined
}

const parseReviews = (value: unknown): KestrelDefectReviews => {
  const reviews: KestrelDefectReviews = {}
  for (const [defectId, rawReview] of Object.entries(parseRecord(value))) {
    const review = parseRecord(rawReview)
    const status = parseReviewStatus(review.status)
    if (!status) continue
    reviews[defectId] = {
      best_image: parseString(review.best_image) ?? null,
      class_name_override: parseString(review.class_name_override) ?? null,
      status,
    }
  }
  return reviews
}

export const parseKestrelDefectDetectionRunMetadata = (
  value: unknown
): KestrelDefectDetectionRunMetadata => {
  const metadata = parseRecord(value)
  return {
    state: parseRunState(metadata.state),
    created_at: parseString(metadata.created_at),
    completed_at: parseString(metadata.completed_at),
    deconfliction_completed_at: parseString(
      metadata.deconfliction_completed_at
    ),
    model: parseString(metadata.model),
    model_type: parseString(metadata.model_type),
    vertex_model_id: parseString(metadata.vertex_model_id),
    confidence_threshold: parseFiniteNumber(metadata.confidence_threshold),
    clustering_distance_m: parseFiniteNumber(metadata.clustering_distance_m),
    error: parseString(metadata.error),
    failure_stage: parseString(metadata.failure_stage),
    image_count: parseFiniteNumber(metadata.image_count),
  }
}

export const isKestrelDefectDetectionRunComplete = (
  metadata: KestrelDefectDetectionRunMetadata
): boolean =>
  metadata.state === KestrelDefectDetectionRunState.DeconflictionComplete

export const isKestrelDefectDetectionRunTerminal = (
  metadata: KestrelDefectDetectionRunMetadata
): boolean =>
  metadata.state === KestrelDefectDetectionRunState.DeconflictionComplete ||
  metadata.state === KestrelDefectDetectionRunState.Failed

export const getKestrelDefectDetectionRunCompletedAt = (
  metadata: KestrelDefectDetectionRunMetadata
): string | undefined =>
  metadata.deconfliction_completed_at ??
  metadata.completed_at ??
  metadata.created_at

export class GeckoKestrelDefectDetectionRun extends GeckoEntryBase<
  KestrelDefectDetectionRunData,
  { 'kestrel-model': string }
> {
  private constructor(json: RolodexEntryJson) {
    const data = parseRecord(json.data)
    super(
      json,
      {
        run_id: parseString(data.run_id) ?? '',
        metadata: parseKestrelDefectDetectionRunMetadata(data.metadata),
        result: parseRecord(data.result),
        reviews: parseReviews(data.reviews),
      },
      {
        'kestrel-model':
          parseString(json.tags[RolodexEntryType.KESTREL_MODEL]) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoKestrelDefectDetectionRun(json)
  }
}
